import React, { useCallback, useEffect } from 'react';
import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import ModalPortal from '../../../../../components/modal-portal';
import CancelSubscriptionModal from './cancellation-modal';
import { usePaymentAndSubscriptionPageContext } from '../../context/payment-and-subscription-page-context';
import { CANCEL_ACCOUNT_PAGE, CANCEL_ACCOUNT_VIA_SUPPORT, DEFAULT_CANCEL_REASON_ID } from '../../constants';
import { getClientsDataAsync } from '../../../clients-3pl/get-clients-data-async';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import styles from '../../style.module.less';
import { Segment } from '../../../../../services/analytics-services/segment';
import { CANCEL_SUBSCRIPTION_LINK_CLICKED } from '../../../../../constants/analytics-events';

const CancelSubscriptionButton = ({ children, brightbackError }) => {
  const {
    isCancellationModalOpen,
    is3plPlanEnabled,
    setIsCancellationModalOpen,
    setCurrentPage,
    setComments,
    setFollowUpExplain,
    setCancelReasonId,
  } = usePaymentAndSubscriptionPageContext();

  const [clientData, setClientData] = useAsyncSafeState({});

  const setCancellationModalState = useCallback(
    (cancelViaSupport = false) => {
      setIsCancellationModalOpen(true);
      Segment.trackEvent(CANCEL_SUBSCRIPTION_LINK_CLICKED, {
        source: 'settings-accounts-subscription- cancel subscription link',
        brightbackError,
        cancelViaSupport,
        clientData,
      });
    },
    [brightbackError, clientData, setIsCancellationModalOpen],
  );

  const openCancellationModal = useCallback(() => {
    setCurrentPage(CANCEL_ACCOUNT_PAGE);
    setCancellationModalState();
  }, [setCurrentPage, setCancellationModalState]);

  const openCancellViaSupportModal = useCallback(() => {
    setCurrentPage(CANCEL_ACCOUNT_VIA_SUPPORT);
    setCancellationModalState(true);
  }, [setCurrentPage, setCancellationModalState]);

  const onClose = useCallback(() => {
    setCurrentPage(CANCEL_ACCOUNT_PAGE);
    setComments('');
    setFollowUpExplain('');
    setCancelReasonId(DEFAULT_CANCEL_REASON_ID);
    setIsCancellationModalOpen(false);
  }, [setCurrentPage, setComments, setFollowUpExplain, setCancelReasonId, setIsCancellationModalOpen]);

  useEffect(() => {
    if (is3plPlanEnabled) {
      getClientsDataAsync().then(setClientData);
    }
  }, [is3plPlanEnabled, setClientData]);

  const shouldDirect3PLUserToSupport = is3plPlanEnabled && (clientData.activeClients?.length > 0 || clientData.errorFetchingClients);

  const onClickProp = shouldDirect3PLUserToSupport ? openCancellViaSupportModal : openCancellationModal;

  useOnComponentMount(() => {
    if (brightbackError) {
      onClickProp();
    }
  });

  return (
    <>
      <Button className={styles.small} onClick={onClickProp} theme='link'>
        {children}
      </Button>

      {isCancellationModalOpen && (
        <ModalPortal onClose={onClose}>
          <CancelSubscriptionModal clientData={clientData} onClose={onClose} />
        </ModalPortal>
      )}
    </>
  );
};

CancelSubscriptionButton.propTypes = {
  children: PropTypes.node.isRequired,
  brightbackError: PropTypes.string,
};

export default CancelSubscriptionButton;
